// Calendario mensual reutilizable. Renderiza días de un mes con decoradores opcionales
// (badges con número de reservas + tono individual/grupal) y soporta click por día.

import { Icon } from '@/components/atoms/Icon';
import styles from './Calendar.module.scss';

export interface DayDecorator {
  individual?: number;
  group?: number;
}

interface CalendarProps {
  month: Date;
  selectedDate?: string | null;
  decorators?: Record<string, DayDecorator>;
  onDayClick?: (isoDate: string) => void;
  onMonthChange?: (delta: -1 | 1) => void;
  canGoPrev?: boolean;
  canGoNext?: boolean;
  highlightToday?: boolean;
}

const WEEKDAYS_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function weekdayMon0(d: Date): number {
  return (d.getDay() + 6) % 7;
}

export function Calendar({
  month,
  selectedDate,
  decorators = {},
  onDayClick,
  onMonthChange,
  canGoPrev = true,
  canGoNext = true,
  highlightToday = true,
}: CalendarProps) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1);
  const lastDay = new Date(year, m + 1, 0);
  const daysInMonth = lastDay.getDate();
  const leadingPad = weekdayMon0(firstDay);
  const totalCells = Math.ceil((leadingPad + daysInMonth) / 7) * 7;
  const today = isoDate(new Date());

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - leadingPad + 1;
    const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
    const date = inMonth ? new Date(year, m, dayNum) : null;
    const dateStr = date ? isoDate(date) : '';
    const dec = inMonth ? decorators[dateStr] : undefined;
    const isToday = highlightToday && dateStr === today;
    const isSelected = dateStr === selectedDate;
    const isPast = inMonth && dateStr < today;
    cells.push(
      <button
        key={i}
        type="button"
        className={`${styles.cell} ${!inMonth ? styles.outside : ''} ${isToday ? styles.today : ''} ${isSelected ? styles.selected : ''} ${isPast ? styles.past : ''}`}
        disabled={!inMonth}
        onClick={() => inMonth && onDayClick?.(dateStr)}
        aria-label={inMonth ? `${dayNum} de ${MONTHS_ES[m]}` : undefined}
      >
        {inMonth && <span className={styles.dayNum}>{dayNum}</span>}
        {dec && (dec.individual || dec.group) && (
          <span className={styles.dots}>
            {dec.individual ? <span className={`${styles.dot} ${styles.dotIndividual}`}>{dec.individual}</span> : null}
            {dec.group ? <span className={`${styles.dot} ${styles.dotGroup}`}>{dec.group}</span> : null}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className={styles.calendar}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.navBtn}
          onClick={() => onMonthChange?.(-1)}
          disabled={!canGoPrev}
          aria-label="Mes anterior"
        >
          <Icon name="chevron-left" size={20} />
        </button>
        <h3 className={styles.title}>
          {MONTHS_ES[m]} <span>{year}</span>
        </h3>
        <button
          type="button"
          className={styles.navBtn}
          onClick={() => onMonthChange?.(1)}
          disabled={!canGoNext}
          aria-label="Mes siguiente"
        >
          <Icon name="chevron-right" size={20} />
        </button>
      </div>

      <div className={styles.weekdays}>
        {WEEKDAYS_ES.map((d) => (
          <div key={d} className={styles.weekday}>{d}</div>
        ))}
      </div>

      <div className={styles.grid}>{cells}</div>
    </div>
  );
}
